import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from '../../components/AuthModal';

interface CategoryCardProps {
  id: string;
  name: string;
  imageUrl: string;
  featuredServices: string[];
  serviceCount: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  id,
  name,
  imageUrl,
  featuredServices,
  serviceCount
}) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const navigate = useNavigate();
  // Imagen de placeholder en caso de que la imagen no cargue
  const placeholderImage = 'https://via.placeholder.com/350x200?text=Categoría';

  // Función para manejar la solicitud de servicio
  const handleServiceRequest = (categoryName: string) => {
    setSelectedCategory(categoryName);
    const authToken = localStorage.getItem('auth_token');
    if (authToken) redirectToChatWithInitialMessage(categoryName);
    else setShowAuthModal(true);
  };

  // Función para manejar la autenticación exitosa
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    if (selectedCategory) redirectToChatWithInitialMessage(selectedCategory);
  };

  // Función para redirigir a ChatPage con el mensaje inicial
  const redirectToChatWithInitialMessage = (categoryName: string) => {
    const initialMessage = `Hola! Tengo la necesidad de un servicio de ${categoryName} y me gustaría que me ayudaras.`;
    localStorage.setItem('initial_chat_message', initialMessage);
    navigate(`/chat?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <div className="flex-shrink-0 w-[250px] bg-white dark:bg-xervix-bg-secondary-dark rounded-xl shadow-lg overflow-hidden mr-4 md:mr-0 md:mx-4">
      {/* Imagen de la categoría */}
      <div className="h-[160px] overflow-hidden">
        <img
          src={imageUrl || placeholderImage}
          alt={`Categoría ${name}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Si la imagen falla al cargar, usar el placeholder
            const target = e.target as HTMLImageElement;
            target.onerror = null; // Prevenir bucle infinito
            target.src = placeholderImage;
          }}
        />
      </div>

      {/* Contenido de la categoría */}
      <div className="p-4">
        {/* Nombre de la categoría */}
        <div className='flex justify-between items-center'>
          <h3 className="text-md font-bold text-gray-800 dark:text-white mb-4">
            {name}
          </h3>
          {/* Contador de servicios */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {serviceCount + " servicios"}
          </p>

        </div>


        {/* Servicios destacados */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
            Servicios destacados:
          </p>
          <ul className="space-y-1">
            {featuredServices.slice(0, 3).map((service, index) => (
              <li
                key={index}
                className="text-gray-600 dark:text-gray-300 text-sm whitespace-pre-line line-clamp-1"
              >
                • {service}
              </li>
            ))}
          </ul>
        </div>



        {/* Botón de solicitar servicios */}
        <button
          onClick={() => handleServiceRequest(name)}
          className="text-xs bg-xervix-primary hover:bg-red-500 text-white font-medium py-1 px-8 rounded-md transition-colors duration-300 block text-center w-full"
        >
          SOLICITAR
        </button>

        {/* Modal de autenticación */}
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={() => handleAuthSuccess()}
          />
        )}
      </div>
    </div>
  );
};

export default CategoryCard;
